import { createReducer } from '@reduxjs/toolkit'

import { Source } from 'types/GlobalSource'
import { insertItems } from 'redux/utils'

import * as actions from '../../../../actions/DataPlatform/connections/Sources/actions'
import * as activeSourceActions from '../../../../actions/DataPlatform/connections/ActiveSources/actions'
import * as authenticationActions from '../../../../actions/Authentication/actions'
import * as sourceTableActions from '../../../../actions/DataPlatform/connections/SourceTables/actions'

import type { TActiveSourcePayload } from '../../../../actions/DataPlatform/connections/Sources/actions'

export type SourceReducerType = {
  data: { [key: string]: Source }
  fetched: boolean
  creating: boolean
  creatingSFTP: boolean
  updating: boolean
  deleting: boolean
  error: boolean
  count: number
}

const INITIAL_STATE: SourceReducerType = {
  data: {},
  fetched: false,
  creating: false,
  creatingSFTP: false,
  updating: false,
  deleting: false,
  error: false,
  count: 0
}

// Only the active source the response was fetched for is replaced.
const mergeSource = (
  state: SourceReducerType,
  payload: TActiveSourcePayload
): Source => {
  const stored = state.data[payload.data.id]

  if (!stored) {
    return payload.data
  }

  return {
    ...payload.data,
    included: {
      ...stored.included,
      active_sources: [
        ...(stored.included.active_sources ?? []).filter(
          (item) => item.id !== payload.activeSourceId
        ),
        ...(payload.data.included.active_sources ?? []).filter(
          (item) => item.id === payload.activeSourceId
        )
      ],
      endpoints: payload.data.included.endpoints
    }
  }
}

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAllSuccess, (state, action) => {
      state.data = insertItems(action.payload.data)
      state.fetched = true
      state.count = action.payload.data.length
    })
    .addCase(actions.getOneSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
    })
    .addCase(activeSourceActions.createSftpSettings, (state) => {
      state.creatingSFTP = true
    })
    .addCase(activeSourceActions.createSftpSettingsSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
      state.creatingSFTP = false
    })
    .addCase(activeSourceActions.createSftpSettingsFailed, (state) => {
      state.creatingSFTP = false
    })
    .addCase(activeSourceActions.create, (state) => {
      state.creating = true
    })
    .addCase(activeSourceActions.createSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
      state.creating = false
    })
    .addCase(activeSourceActions.createFailed, (state) => {
      state.creating = false
    })
    .addCase(activeSourceActions.upload, (state) => {
      state.error = false
      state.creating = true
    })
    .addCase(activeSourceActions.uploadSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
      state.error = false
      state.creating = false
    })
    .addCase(activeSourceActions.uploadFailed, (state) => {
      state.error = true
      state.creating = false
    })
    .addCase(activeSourceActions.resetPasswordSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
    })
    .addCase(activeSourceActions.update, (state) => {
      state.error = false
      state.updating = true
    })
    .addCase(activeSourceActions.updateSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
      state.error = false
      state.updating = false
    })
    .addCase(activeSourceActions.updateFailed, (state) => {
      state.error = true
      state.updating = false
    })
    .addCase(activeSourceActions.destroy, (state) => {
      state.deleting = true
    })
    .addCase(activeSourceActions.destroySuccess, (state, action) => {
      const source = state.data[action.payload.sourceId]

      if (source) {
        source.included.active_sources = source.included.active_sources.filter(
          (item) => item.id !== action.payload.id
        )
      }

      state.deleting = false
    })
    .addCase(sourceTableActions.updateSuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
    })
    .addCase(sourceTableActions.destroy, (state) => {
      state.deleting = true
    })
    .addCase(sourceTableActions.destroySuccess, (state, action) => {
      state.data[action.payload.data.id] = mergeSource(state, action.payload)
      state.deleting = false
    })
    .addCase(sourceTableActions.destroyFailed, (state) => {
      state.deleting = false
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
